import { useCallback, useMemo } from 'react'
import { documentationRootGuid } from '@constants'
import { Divider, Stack } from '@mui/material'
import { Form2 } from '@ui/forms'
import BaseFormFields from '../../../BaseFormFields'
import PageSelectionField from '../../../PageSelectionField'
import {
  formatPageSelection,
  createPageSelectionSchema,
  withParsedPages,
} from '../../../PageSelectionField/utils'
import FormFields from './components/FormFields'

type DefaultValues = PdfFileItemConfig & {
  file: File | null
  page_selection: string
}

interface Props {
  item: FileItem
  mutate: (data: PdfFileItemConfig & { file: File | null }) => void
  onCropConfigChange: (config: {
    croppedLeft: number
    croppedTop: number
    croppedRight: number
    croppedBottom: number
  }) => void
  pageCount?: number
  /** Called with the valid page selection while it is edited, before it is saved */
  onPagesChange?: (itemGuid: string, pages: number[] | null) => void
}

const PDFFileInfo = ({ item, mutate, onCropConfigChange, pageCount, onPagesChange }: Props) => {
  const defaultValues = useMemo(() => {
    const baseConfig: DefaultValues = {
      ...(item.additional_data ?? {}),
      item_guid: item.guid,
      name: item.name,
      bu: item?.additional_data?.bu ?? 0,
      bb: item?.additional_data?.bb ?? 0,
      bl: item?.additional_data?.bl ?? 0,
      br: item?.additional_data?.br ?? 0,
      preamble: item?.additional_data?.preamble ?? '',
      page_selection: formatPageSelection(item?.additional_data?.pages),
      file: null,
    }

    return baseConfig
  }, [item])

  const validationSchema = useMemo(
    () => createPageSelectionSchema(pageCount, defaultValues.page_selection),
    [pageCount, defaultValues.page_selection],
  )

  const onSubmit = useCallback(
    (data: DefaultValues) => {
      const config = withParsedPages(data)
      onPagesChange?.(item.guid, config.pages)
      mutate(config)
    },
    [mutate, onPagesChange, item.guid],
  )

  return (
    <Stack spacing={2} p={1}>
      <Form2
        key={JSON.stringify(defaultValues)}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        validationSchema={validationSchema}
        submitOnChange
      >
        <Stack spacing={2}>
          <BaseFormFields
            elementGuid={item.element_guid}
            showBeginOnNewPage={item.parent_guid !== documentationRootGuid}
          />

          <Divider />

          <PageSelectionField pageCount={pageCount} />

          <FormFields onCropConfigChange={onCropConfigChange} />
        </Stack>
      </Form2>
    </Stack>
  )
}

export default PDFFileInfo
